import { all, put, takeEvery, takeLatest } from "@redux-saga/core/effects";
import {
  createLocation,
  getAveragePercentageOfTotalWorkforceSummary,
  getComplaintSummary,
  getEmployeeCapturesByLocationId,
  updateLocationImageUrl,
  updatelocationStatusinDB
} from "../../Services/database";
import {
  getFileUrl,
  uploadLocationImageToStorage
} from "../../Services/storage";
import { setErrorStatus, setSuccessStatus } from "../status/action";
import store from "../store";
import { fetchLocationsAssets, setSelectedLocationData } from "./action";

export const actionTypes = {
  SET_LOCATIONS_DATA: "SET_LOCATIONS_DATA",
  SET_LOCATIONS_RESULTS: "SET_LOCATIONS_RESULTS",
  SET_ZONE_AND_WARD: "SET_ZONE_AND_WARD",
  CREATE_LOCATION: "CREATE_LOCATION",
  GET_COMPLAINT_SUMMARY_DATA: "GET_COMPLAINT_SUMMARY_DATA",
  PUT_LOCATION_SUMMARY_DATA: "PUT_LOCATION_SUMMARY_DATA",
  GET_LOCATION_SUMMARY: "GET_LOCATION_SUMMARY",
  UPLOAD_LOCATION_IMAGE: "UPLOAD_LOCATION_IMAGE",
  FETCH_LOCATIONS_ASSETS: "FETCH_LOCATIONS_ASSETS",
  FETCH_SELECTED_LOCATION_DATA: "FETCH_SELECTED_LOCATION_DATA",
  GENERATE_ATTENDANCE_FROM_CAPTURES: "GENERATE_ATTENDANCE_FROM_CAPTURES",
  SET_SELECTED_LOCATION_DATA: "SET_SELECTED_LOCATION_DATA",
  UPDATE_LOCATION_STATUS:"UPDATE_LOCATION_STATUS"
};

function* setLocationsDataWorker(action) {
  yield setLocationsLoading(true);

  yield put({
    type: "PUT_LOCATIONS_DATA",
    payload: {
      data: action.payload.data
    }
  });

  yield setLocationsLoading(false);
}

function* setLocationsResultsWorker(action) {
  yield setLocationsLoading(true);

  yield put({
    type: "PUT_LOCATIONS_RESULTS",
    payload: {
      data: action.payload.data
    }
  });

  yield put({
    type: "SET_LOCATIONS_COUNT",
    payload: {
      count: action.payload.count
    }
  });

  yield setLocationsLoading(false);
}

function* getComplaintSummaryWorker() {
  try {
    yield setLocationsLoading(true);
    const summaryData = yield getComplaintSummary();
    yield put({
      type: "SET_COMPLAINT_SUMMARY_DATA",
      payload: { data: summaryData }
    });
    yield setLocationsLoading(false);
  } catch (error) {
    yield setLocationsLoading(false);
    console.error(error, "from getComplaintSummaryWorker");
    setErrorStatus(error);
  }
}

function* createLocationWorker(action) {
  try {
    yield setLocationsLoading(true);
    yield createLocation(action.payload.data);
    setSuccessStatus("Location Added Successfully");
    yield setLocationsLoading(false);
  } catch (error) {
    yield setLocationsLoading(false);
    console.error(error);
    setErrorStatus(error);
  }
}

function* putLocationSummaryWorker(action) {
  yield setLocationsLoading(true);

  yield put({
    type: "SET_GRAPH_SUMMARY_DATA",
    payload: {
      manPower: {
        captured: action.payload.manPowerAverage,
        lastEmployeeCaptured: action.payload.lastManPowerCapturedAverage
      },
    }
  });

  yield setLocationsLoading(false);
}

function* getLocationSummaryWorker() {
  try {
    yield setLocationsLoading(true);

    let zoneCounts = {};

    const locations = store.getState().locations;
    const auth = store.getState().auth;

    if (locations.data) {
      for (const data of Object.values(locations.data)) {
        zoneCounts[data.zone] = zoneCounts[data.zone]
          ? zoneCounts[data.zone] + 1
          : 1;
      }
    }

    // Attendance — unique-employee-present-today count. This used to be a
    // "uniqueCount" over captures spanning the shift-hours window (via /summary,
    // which Firestore can't do natively). attendanceSummary already has one doc
    // per employee per day, so counting docs with percentage > 0 for today is the
    // same figure without a distinct-count aggregation.
    if (auth.data.uid) {
      const now = new Date();
      const { totalPresent } = yield getAveragePercentageOfTotalWorkforceSummary({
        from: now,
        to: now,
        type: "Today"
      });

      const totalEmployees = store.getState().workforce.data
        ? Object.values(store.getState().workforce.data).length
        : 0;

      yield put({
        type: "SET_GRAPH_SUMMARY_DATA",
        payload: {
          zoneCounts: zoneCounts,
          employeeAttendance: {
            present: totalPresent,
            absent: totalEmployees - totalPresent,
            total: totalEmployees
          }
        }
      });
    }

    yield setLocationsLoading(false);
  } catch (error) {
    setErrorStatus(error);
    yield setLocationsLoading(false);
  }
}

function* setWardAndZoneWorker(action) {
  yield put({
    type: "PUT_ZONE_AND_WARD",
    payload: {
      data: action.payload.data
    }
  });
}
function* uploadLocationImageWorker(action) {
  try {
    yield setLocationsLoading(true);
    const uploadedImages = yield uploadLocationImageToStorage(
      action.payload.data,
      action.payload.locationId
    );
    const existingImages =
      store.getState().locations.data[action.payload.locationId]?.assets || [];
    const images = [...existingImages, uploadedImages];
    yield updateLocationImageUrl(action.payload.locationId, images);
    yield fetchLocationsAssets(action.payload.locationId);
    yield setSuccessStatus("Image Uploaded Successfully");
    yield setLocationsLoading(false);
  } catch (error) {
    yield setLocationsLoading(false);
    console.error(error);
    setErrorStatus(error);
  }
}
function* fetchLocationsAssetsWorker() {
  try {
    yield setLocationsLoading(true);
    const locationId = store.getState().table.locations.selectedLocation;
    const assets = store.getState().locations.data[locationId]?.assets || [];
    let images = [];
    for (const asset of assets) {
      const file = yield getFileUrl(asset);
      if (file.type === "image") {
        images.push(file.url);
      }
    }
    yield put({
      type: "SET_LOCATION_ASSETS",
      payload: {
        data: {
          ...store.getState().locations.data[locationId],
          fileSrc: { images: images }
        }
      }
    });
    yield setLocationsLoading(false);
  } catch (error) {
    yield setLocationsLoading(false);
    console.error(error);
    setErrorStatus(error);
  }
}

function* fetchSelectedLocationDataWorker(action) {
  try {
    const duration =
      action.payload.dateRange.to - action.payload.dateRange.from;
    const oneDayInMs = 24 * 60 * 60 * 1000;
    const isFullMonth = duration > oneDayInMs;

    yield setSelectedLocationData({
      loading: {
        attendance: isFullMonth,
        capture: true
      }
    });

    const locationId = store.getState().table.locations.selectedLocation;
    const data = store.getState().locations.data[locationId];
    // const workforceData = yield getWorkforceByLocationId(locationId)
    // const procedureHistoryData = yield getProcedureHistoryByLocationId(locationId)
    // const complaintsData = yield getComplaintsByLocationId(locationId)
    const capturesData = yield getEmployeeCapturesByLocationId(
      locationId,
      action.payload.dateRange
    );

    yield setSelectedLocationData({
      data: data,
      // complaints: complaintsData,
      // procedureHistory: procedureHistoryData,
      // workforce: workforceData,
      // summaryData: mockSummaryData,
      // attendance: attendanceData,
      captures: capturesData
    });

    yield setSelectedLocationData({
      loading: {
        attendance: isFullMonth,
        capture: false
      }
    });

    if (isFullMonth) {
      const dayCount = {};

      for (const capture of Object.values(capturesData)) {
        const date = new Date(capture.createdAt);
        const year = String(date.getFullYear());
        const month = String(date.getMonth());
        const day = String(date.getDate());

        if (!dayCount[year]) dayCount[year] = {};
        if (!dayCount[year][month]) dayCount[year][month] = {};
        if (!dayCount[year][month][day]) {
          dayCount[year][month][day] = { totalPercentage: 1 };
        }
      }

      yield setSelectedLocationData({
        attendance: dayCount
      });
    }

    yield setSelectedLocationData({
      loading: {
        attendance: false,
        capture: false
      }
    });

    if (Object.values(capturesData).length > 0) {
      for (const capture of Object.values(capturesData)) {
        if (typeof capture.fileSrc === "string") {
          continue;
        }
        const fileSrc = yield getFileUrl(capture.asset);
        yield put({
          type: "SET_SELECTED_LOCATION_DATA_CAPTURE_ASSETS",
          payload: {
            ...capture,
            fileSrc: fileSrc.url
          }
        });
      }
    }

  } catch (error) {
    yield setSelectedLocationData({
      loading: {
        attendance: false,
        capture: false
      }
    });
    console.error(error);
    setErrorStatus(error);
  }
}

export function* setSelectedLocationDataWorker(action) {
  if (action.payload === null) {
    yield put({
      type: "PUT_SELECTED_LOCATION_DATA",
      payload: {
        data: null,
        captures: null,
        attendance: null,
        loading: { captures: false, attendance: false }
      }
    });
    return;
  }

  yield put({
    type: "PUT_SELECTED_LOCATION_DATA",
    payload: { data: action.payload }
  });
}

function* updateLocationStatusWorker(action) {
  try {
    yield setLocationsLoading(true);
    const success = yield updatelocationStatusinDB(
      action.payload.locationId,
      action.payload.status,
      action.payload.reason
    );

    if (success) {
      const locationData = store.getState().locations.data[action.payload.locationId];
      yield setSelectedLocationData({
        data: {
          ...locationData,
          operationalStatus: {
            state: action.payload.status,
            reason: action.payload.status === "Inactive" ? action.payload.reason : ""
          }
        }
      });
    }

    yield setLocationsLoading(false);
  } catch (error) {
    yield setLocationsLoading(false);
    console.error(error, "error updating location status");
  }
}


export default function* locationsWatcher() {
  yield all([
    takeEvery("CREATE_LOCATION", createLocationWorker),
    takeEvery("SET_LOCATIONS_DATA", setLocationsDataWorker),
    takeEvery("SET_LOCATIONS_RESULTS", setLocationsResultsWorker),
    takeEvery("SET_ZONE_AND_WARD", setWardAndZoneWorker),
    takeEvery("GET_COMPLAINT_SUMMARY_DATA", getComplaintSummaryWorker),
    takeEvery("PUT_LOCATION_SUMMARY_DATA", putLocationSummaryWorker),
    takeEvery("GET_LOCATION_SUMMARY", getLocationSummaryWorker),
    takeEvery("UPLOAD_LOCATION_IMAGE", uploadLocationImageWorker),
    takeEvery("FETCH_LOCATIONS_ASSETS", fetchLocationsAssetsWorker),
    takeLatest("FETCH_SELECTED_LOCATION_DATA", fetchSelectedLocationDataWorker),
    takeLatest("SET_SELECTED_LOCATION_DATA", setSelectedLocationDataWorker),
    takeEvery("UPDATE_LOCATION_STATUS", updateLocationStatusWorker)
  ]);
}

function* setLocationsLoading(bool) {
  yield put({
    type: "SET_LOCATIONS_LOADING",
    payload: {
      loading: bool
    }
  });
}
